import { autoinject, transient, computedFrom, observable, useView } from "aurelia-framework";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import { GhgVerificationCiiModel, GhgVerificationClientModel, GhgVerificationStatusEnum, GhgVerificationCiiFactorClientModel } from "../../../../../../models/ghgs/ghg.models";
import { NotificationType, NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Assignment } from "../../../../../components/assignment/assignment";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { AttachmentsTypeGuids } from "../../../../../../constants/modules/attachments-type-guids";
import { LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { RightshipEntityAttachmentManager } from "../../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { GhgCiiFactorsRenderer } from "../../helpers/ghg-cii-factors-renderer";
import * as Enumerable from "linq";

@autoinject()
@transient()
@useView('views/pages/modules/ghg/steps/ghg-cii/ghg-cii.html')
export class GhgCii {
    public ghgVerification: GhgVerificationClientModel;
    public originalGhgVerificationCii: GhgVerificationCiiModel;
    public assignment: Assignment;
    public rightshipEntityAttachmentManager: RightshipEntityAttachmentManager;
    public customerId: number;
    public originalCiiSnapshot: string;
    public rsGhgRatingFactorSources: { id: number, name: string }[]
    public ghgCiiFactorsRenderer: GhgCiiFactorsRenderer;
    public ciiGroups: string[] = [];
    public ciiFactorsByGroup: { [group: string]: GhgVerificationCiiFactorClientModel[] } = {};
    public allCiiFactors: GhgVerificationCiiFactorClientModel[] = [];
    public originalCiiFactors: GhgVerificationCiiFactorClientModel[];
    public ciiSelected: boolean = false;

    @observable
    allSelected: boolean = false;

    @computedFrom('ghgVerification.isClosed', 'customerId', 'ghgVerification.assignedUserId')
    public get canSave() {
        return this.ghgVerification && !this.ghgVerification.isClosed && !this.customerId && this.ghgVerification.assignedUserId == this.userClientService.currentUser.id;
    }

    @computedFrom('ciiSelected')
    get someSelected() {
        return this.ciiSelected || this.allSelected;
    }

    @computedFrom('rightshipEntityAttachmentManager.items', 'rightshipEntityAttachmentManager.items.length')
    public get documents() {
        const types = [
            AttachmentsTypeGuids.Cii.toLowerCase(),
            AttachmentsTypeGuids.CiiCorrectiveActionPlan.toLowerCase()
        ]
        if (!this.rightshipEntityAttachmentManager || !this.rightshipEntityAttachmentManager.items)
            return [];

        return this.rightshipEntityAttachmentManager.items.filter(x => types.some(y => x.attachmentTypeGuid && y == x.attachmentTypeGuid.toLowerCase()));
    }

    constructor(private ghgService: GhgService,
        private notificationClientService: NotificationClientService,
        private userClientService: UserClientService,
        private dialogClientService: DialogClientService,
        private validationsClientService: ValidationsClientService,
        private lookupsClientService: LookupClientService) {
        this.customerId = userClientService.currentUser.settings.customerId;
    }

    public activate(model: { ghgVerification: GhgVerificationClientModel, assignment: Assignment, rightshipEntityAttachmentManager: RightshipEntityAttachmentManager }) {

        if (!model.ghgVerification) {
            return;
        }

        this.ghgVerification = model.ghgVerification;
        this.assignment = model.assignment;
        this.rightshipEntityAttachmentManager = model.rightshipEntityAttachmentManager;
        this.ghgService.getGhgVerificationCiiFactorsClientModel(this.ghgVerification.ghgVerificationId).then(factors => {
            const factorsDic: { [group: string]: GhgVerificationCiiFactorClientModel[] } = {};

            this.ghgCiiFactorsRenderer = new GhgCiiFactorsRenderer(factors);

            this.ciiGroups = Enumerable.from(factors).select(x => x.group).distinct().toArray();
            this.ciiGroups.forEach(group => {
                factorsDic[group] = [];
            });

            this.ghgCiiFactorsRenderer.orderedFactors.forEach(factor => {
                factorsDic[factor.group].push(<GhgVerificationCiiFactorClientModel>factor);
            });

            this.ciiFactorsByGroup = factorsDic;
            this.allCiiFactors = factors;
            this.originalCiiFactors = CloneHelper.cloneObject(factors);
            this.originalCiiSnapshot = JSON.stringify(factors.map(x => (
                {
                    passed: x.passed,
                    ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                    revisedValue: x.revisedValue
                })));
        });
        this.loadData();
    }

    loadData() {
        const lookupValueRequest = new LookupValueRequest();
        lookupValueRequest.lookupValueQuery = 'RS_GhgRatingFactorSources';
        lookupValueRequest.parameters = ""
        this.lookupsClientService.getLookup(lookupValueRequest, 'ghg-verification')
            .then(response => {
                this.rsGhgRatingFactorSources = response['_values'].map(source => { return { id: source.id, name: source.name } });
            })
    }

    hasChanges() {
        return this.canSave &&
        (this.originalCiiSnapshot && this.originalCiiSnapshot != JSON.stringify(this.allCiiFactors.map(x => (
            {
                passed: x.passed,
                ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                revisedValue: x.revisedValue
            })))
        );
    }

    discardChanges() {
        this.allCiiFactors = CloneHelper.cloneObject(this.originalCiiFactors);
    }

    save() {
        return new Promise<void>((resolve, reject) => {

            if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
                this.ghgService.saveGhgVerificationCii({
                    ciiFactors: this.allCiiFactors,
                    ghgVerificationId: this.ghgVerification.ghgVerificationId
                }).then(response => {
                    if (response.validationResult && response.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(response.validationResult);
                        reject();
                    } else {
                        this.originalCiiSnapshot = JSON.stringify(this.allCiiFactors.map(x => ({ passed: x.passed, ghgRatingFactorSourceId: x.ghgRatingFactorSourceId, revisedValue: x.revisedValue })));
                        this.originalCiiFactors = CloneHelper.cloneObject(this.allCiiFactors);
                
                        if(response.validationResult && response.validationResult.warnings) {
                            response.validationResult.warnings.forEach(x => {
                                this.notificationClientService.show("Alert!", x.message, NotificationType.Warning);
                            });
                        }
                
                        this.notificationClientService.show("CII saved successfully", "", NotificationType.Success);
                        resolve();
                    }
                }).catch(() => {
                    this.notificationClientService.show("Failed to save CII", "", NotificationType.Error);
                    reject();
                });
            } else {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                    name: "Environmental Review Request",
                    submit: this.assignToMe,
                    callback: () => {
                        this.save().then(resolve).catch(reject);
                    },
                    onCancel: () => reject(),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
            }
        });
    }

    assignToMe = () => {
        return new Promise<void>((resolve) => {
            if (this.assignment) {
                this.assignment.assignToMe().then(() => {
                    this.ghgVerification.ghgVerificationStatusId = GhgVerificationStatusEnum.InProgress;
                    this.ghgVerification.assignedUserId = this.userClientService.currentUser.id;
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    public getCiiMenuItems(item: GhgVerificationCiiModel): MenuItem[] {
        return [
            {
                click: () => this.showCiiComments(item),
                isVisible: true,
                item: {
                    text: 'Comments'
                },
                classList: '',
            }
        ];
    }

    showCiiComments(item: GhgVerificationCiiModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationCiiComments, {
            ghgVerificationCii: item,
            ghgVerification: this.ghgVerification
        }, true, false, false, 'right');
    }

    allSelectedCheckedChanged(event) {
        this.allSelected = event.detail.value;
        this.ciiSelected = this.allSelected;
        this.allCiiFactors.forEach(factor => {
            factor.isSelected = this.allSelected;
        });
    }

    updateOutcome(value: boolean, factor: GhgVerificationCiiFactorClientModel) {
        if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
            if (!this.canSave)
                return;

            if (factor)
                factor.passed = value;
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                name: "Environmental Review Request",
                submit: this.assignToMe,
                callback: () => {
                    this.updateOutcome(value, factor);
                },
                currentAssignment: this.assignment.assignedEntity.assignedToUserName
            }, true, false, false);
        }
    }

    setSelectedAsPassed() {
        if (!this.canSave) {
            return;
        }

        this.allCiiFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = true;
            }
            factor.isSelected = false;
        });

        this.allSelected = false;
        this.ciiSelected = false;
    }

    setSelectedAsFailed() {
        if (!this.canSave) {
            return;
        }

        this.allCiiFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = false;
            }
            factor.isSelected = false;
        });

        this.allSelected = false;
        this.ciiSelected = false;
    }

    setSelectedAsNotApplicable() {
        if (!this.canSave) {
            return;
        }

        this.allCiiFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = null;
            }
            factor.isSelected = false;
        });

        this.allSelected = false;
        this.ciiSelected = false;
    }

    setCiiRating(letter: string) {
        const ciiRatingIndex = this.allCiiFactors.findIndex(x => x.propertyName == "CiiRating");
        if (this.canSave) {
            this.allCiiFactors[ciiRatingIndex].revisedValue = letter; 
        }
    }

    //Check if some of the checkboxes are selected
    onSelectedFactor() {
        this.ciiSelected = this.allCiiFactors.some(factor => factor.isSelected );
    }
}
